'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import { observer } from 'mobx-react';
import {
  DateInputType,
  Overlay,
  PackageModule,
  PackageParams,
  PackageSearchParams,
  PackageSearchStore,
  useAppService,
  useClientPersistSearchParams,
  useConfig,
  useMediaQuery,
  useWindow
} from '@ibe/components';
import {
  ApiBestPricePackageModel,
  ApiDurationType,
  ApiFilterCategory,
  ApiGeoUnit,
  ApiGetPackagesResponse,
  ApiItemType,
  ApiListOptions,
  ApiPriceModifierType,
  ApiProductData,
  ApiSortOption
} from '@ibe/api';
import { usePathname, useRouter } from 'next/navigation';
import useQuery from '../../Util/useQuery';
import PackageParamsTransformer from '../../Util/ParamsTransformer/PackageParamsTransformer';
import { stepSessionStorage } from '@/Util/globals';
import RenderSearchContent from './render/RenderSearchContent';
import renderSearch from './render/RenderSearch';
import RenderListItem from './render/RenderListItem';
import BestPriceSearchContent from './render/BestPriceSearchContent';
import { configService, productSearchSessionKey } from '@/Config/config';
import { IbeProps } from '@/components/ibe/IbeProps';
import useRemountHooks from '@/components/ibe/useRemountHook';
import PackageDetails from '@/components/PackageDetails/PackageDetails';
import PriceDetails from '@/components/PackageDetails/PriceDetails';
import { useMount } from 'react-use';
import {
  calculateMissingEndDate,
  enrichSearchParamsCodes,
  fetchDestinationsGeoUnits,
  fetchDestinationsProducts,
  fetchOriginsGeoUnits,
  UrlEnrichmentData
} from '@/components/ibe/IbeUtils';
import Keys from '@/Translations/generated/en/ibe.json.keys';
import { Button, Modal, ModalBody, ModalFooter } from 'reactstrap';
import { PrintTableWrapper } from '../PackageDetails/PrintInfo';
import InternalFlightModalWarning from './render/InternalFlightInfo/InternalFlightModalWarning';
import { areNumberOfTravelersBiggerThanMax, showModalOrNot } from './ibeSearch/IbeSearch';
import YsnConfigModel from '@/Config/YsnConfigModel';
import { useTranslation } from '@/app/i18n/client';
import { getLinkUrl } from '../linkList/Link';
import { useApi } from '@/Util/useApi';
import dayjs from 'dayjs';

const CustomIndexedDestinationSelect = dynamic(
  () => import('./render/CustomIndexedDestinationSelect'),
  {
    ssr: false
  }
);

const durationProps = {
  withDuration: true,
  value: { duration: 7, type: ApiDurationType.DAY },
  usePlaceholderAsOption: true
};

const Ibe = observer(function Ibe(props: IbeProps) {
  const {
    checkoutUrl,
    internalFlightLogo,
    store,
    customPackageSearchStore,
    isLoading,
    setIsLoading
  } = props;
  const router = useRouter();
  const pathname = usePathname();
  const query = useQuery();
  const tempQuery = useRef<URLSearchParams>(null);
  const api = useApi();
  const config = useConfig() as YsnConfigModel;
  const window = useWindow();
  const appService = useAppService();
  const { t } = useTranslation('ibe');
  const persistentSearchParams = useClientPersistSearchParams<Partial<PackageParams>>() || {};
  const [searchClicked, setSearchClicked] = useState(true); // initially true, bc clicked on IbeSearch
  const [hasBookingError, setHasBookingError] = useState(false);
  const [urlEnrichmentData, setUrlEnrichmentData] = useState<UrlEnrichmentData | undefined>(
    undefined
  );
  const [searchParams, setSearchParams] = useState<Partial<PackageParams> | undefined>(undefined);
  const [expandForPrint, setExpandForPrint] = useState<boolean>(false);
  const isMounted = useRef<boolean>(false);
  customPackageSearchStore.setShowValidationErrorMessage(true);
  const paxRoomSelectionImperativeHandleRef = useRef<{
    toggleDropdownOnly?: (a: boolean) => void;
  }>(null);
  const [isOpenMaxOccupancyModal, setIsOpenMaxOccupancyModal] = useState(false);
  const [ysnFlightOnly, setYsnFlightOnly] = useState(false);
  const [isSearchButtonClicked, setIsSearchButtonClicked] = useState(false);

  const isMobile = useMediaQuery({ query: '576', type: 'max' });

  useEffect(() => {
    customPackageSearchStore.validateParamsAndShowError();
  }, [isMobile]);

  useEffect(() => {
    if (isMobile && !isLoading) {
      adjustLayoutForMobile();
    }
  }, [isLoading, isMobile]);

  const handlePrint = useCallback(() => {
    setExpandForPrint(true);
    setTimeout(() => {
      window?.print();
    }, 500);
    setTimeout(() => {
      setExpandForPrint(false);
    }, 850);
  }, []);

  const fetchDestination = async (onlyProduct?: boolean) => {
    const paramsDecoded = PackageParamsTransformer.decode(Object.fromEntries(query.entries()));
    const req = [
      fetchDestinationsProducts(api, paramsDecoded.destinations || []),
      onlyProduct ? null : fetchDestinationsGeoUnits(api)
    ];
    return Promise.all(req);
  };
  const [keys, forceRemount] = useRemountHooks();

  useMount(async () => {
    setIsLoading(true);
    const [productsRes, geoUnitsRes] = await fetchDestination();
    const origins = await fetchOriginsGeoUnits(api, appService.lang);
    setUrlEnrichmentData({
      destinations: {
        geoUnits: geoUnitsRes as ApiGeoUnit[],
        products: productsRes as ApiProductData[]
      },
      origins
    });
    isMounted.current = true;
    setIsLoading(false);
  });

  useEffect(() => {
    (async () => {
      if (!searchClicked) return;
      let newUrlEnrichmentData: UrlEnrichmentData | undefined = undefined;
      setIsLoading(true);
      const params = persistentSearchParams.getSearchParams
        ? persistentSearchParams.getSearchParams(pathname)
        : undefined;
      let paramsDecoded = PackageParamsTransformer.decode(Object.fromEntries(query.entries()));

      paramsDecoded.dateInputType = DateInputType.FULL;
      if (paramsDecoded.mainServiceCodes?.length && isMounted) {
        const [productsRes] = await fetchDestination(true);
        newUrlEnrichmentData = {
          ...urlEnrichmentData,
          destinations: {
            ...urlEnrichmentData?.destinations,
            products: productsRes as ApiProductData[]
          }
        } as UrlEnrichmentData;
      }
      if (urlEnrichmentData) {
        paramsDecoded = enrichSearchParamsCodes(
          paramsDecoded,
          newUrlEnrichmentData || urlEnrichmentData
        );
      } else {
        setIsLoading(false);
        return;
      }
      const searchParameters =
        Object.keys(Object.fromEntries(query.entries())).length > 0
          ? {
              ...paramsDecoded,
              ...params
            }
          : { ...params };
      setSearchParams(calculateMissingEndDate(searchParameters, config.formatDate));
      setIsLoading(false);
      if (
        !tempQuery.current ||
        (!!tempQuery.current && query.toString() !== tempQuery.current?.toString())
      ) {
        setSearchClicked(false);
      }
      if (newUrlEnrichmentData) {
        setUrlEnrichmentData(newUrlEnrichmentData);
      }
      if (paramsDecoded.inventories) {
        setYsnFlightOnly(true);
      }
      tempQuery.current = query;
    })();
  }, [
    !!persistentSearchParams.getSearchParams,
    query.toString(),
    JSON.stringify(urlEnrichmentData),
    searchClicked
  ]);

  const DetailContent = useMemo(() => {
    return {
      MainContent: (props: {
        item: ApiBestPricePackageModel;
        searchParams: PackageSearchParams;
      }) => <PackageDetails {...props} expandForPrint={expandForPrint} />,
      PriceDetails: (props: {
        item: ApiBestPricePackageModel;
        searchParams: PackageSearchParams;
      }) => <PriceDetails {...props} onPrintClick={handlePrint} />
    };
  }, [expandForPrint]);

  const updateUrlParamsOrShowModal = (params: PackageParams, listOptions: ApiListOptions): void => {
    if (showModalOrNot(customPackageSearchStore, store, ysnFlightOnly)) {
      setIsSearchButtonClicked(true);
      return setIsOpenMaxOccupancyModal(
        showModalOrNot(customPackageSearchStore, store, ysnFlightOnly)
      );
    }
    updateUrlParams(params, listOptions);
  };

  const search = useMemo(() => {
    return renderSearch(undefined, customPackageSearchStore);
  }, [customPackageSearchStore]);

  const updateUrlParams = useCallback(
    (rawParams: PackageParams, listOptions: ApiListOptions): void => {
      setIsLoading(true);
      const params = calculateMissingEndDate(rawParams, config.formatDate);

      params.preSelectedCharacteristics = undefined;
      if (ysnFlightOnly) {
        params.inventories = config.internFlightInventories;
      } else {
        params.inventories = undefined;
      }
      const { listOptions: _lisOptions, ...restParams } = params;

      const queryParams = PackageParamsTransformer.encode(restParams);
      if (persistentSearchParams.setSearchParams) {
        persistentSearchParams.setSearchParams(pathname, params);
      }
      const newlistOptions = {
        ...({} as Partial<ApiListOptions>),
        pagination: listOptions.pagination
      };
      window?.history.replaceState(
        {},
        '',
        `${pathname}?${queryParams}&listOptions=${JSON.stringify(newlistOptions)}`
      );
      setTimeout(() => {
        setSearchClicked(true);
      }, 500);
    },
    [ysnFlightOnly, config, persistentSearchParams, window]
  );

  const handleBackHistory = () => {
    if (window && searchParams) {
      if (searchParams) {
        const { itemId, goToDetails, ...updateSearchParams } = searchParams;
        const queryParams = PackageParamsTransformer.encode(updateSearchParams);
        if (persistentSearchParams.setSearchParams) {
          persistentSearchParams.setSearchParams(pathname, updateSearchParams);
        }
        store.resetDetail();
        setSearchParams({ ...updateSearchParams });
        router.push(`${pathname}?${queryParams}`);
        forceRemount();
      }
    }
  };

  const shallowUrlUpdate = (params: PackageParams): void => {
    const queryParams = PackageParamsTransformer.encode(params);
    if (persistentSearchParams.setSearchParams) {
      persistentSearchParams.setSearchParams(pathname, params);
    }
    window?.history.replaceState({}, '', `${pathname}?${queryParams}`);
  };

  const onBestPriceAvailabilitySelect = async (
    _: ApiGetPackagesResponse | null,
    __: PackageParams,
    packageCartId?: string
  ): Promise<void> => {
    if (packageCartId) {
      setIsLoading(true);
      try {
        await api.attemptBooking(packageCartId);
        stepSessionStorage.clear();
        sessionStorage.setItem(productSearchSessionKey, query.toString());
        router.push(getLinkUrl(checkoutUrl ?? '', config));
      } catch (err) {
        console.debug(err);
        setHasBookingError(true);
      }
      setIsLoading(false);
    }
  };

  const onPageChangeListener = useCallback(() => {
    const findSearchContainer = document.getElementsByClassName('iso__serviceList__mobile-section');
    if (window && findSearchContainer[0]) {
      const y = (findSearchContainer[0]?.getBoundingClientRect().top || 0) + window.pageYOffset;
      window.scrollTo({ top: y, behavior: 'smooth' });
    }
  }, []);

  const adjustLayoutForMobile = () => {
    const serviceListItems = document.querySelectorAll('.service-list__item');
    serviceListItems.forEach(item => {
      const internalButton = item.querySelector('.internalFlightInfo');
      const infoPrice = item.querySelector('.package-info__price');
      const discountLabel = item.querySelector('.discount__price')?.parentNode;
      if (internalButton && infoPrice && discountLabel) {
        const containerDiv = document.createElement('div');
        containerDiv.className = 'internal-flight-info-mobile';
        containerDiv.append(internalButton, discountLabel);
        const parentContainer = infoPrice?.parentNode?.parentNode?.parentNode;
        parentContainer?.insertBefore(containerDiv, parentContainer.lastChild);
      }
    });
  };

  function handleBackToListClick() {
    setIsLoading(true);
    handleBackHistory();
    setIsLoading(false);
  }

  const visibilityClassName = useMemo(() => {
    let paramsDecoded = PackageParamsTransformer.decode(Object.fromEntries(query.entries()));
    return isLoading && paramsDecoded.isNewTab && paramsDecoded.goToDetails
      ? 'hide_search_bar_on_new_tab'
      : '';
  }, [query.toString(), isLoading]);

  const onDestinationChange = useCallback(() => {
    let mutableSearchParams = searchParams || {}; // There is a specific reason for this: to avoid unnecessary re-rendering.
    mutableSearchParams.destinations = customPackageSearchStore.searchParams.destinations;
    mutableSearchParams.mainServiceCodes = customPackageSearchStore.searchParams.mainServiceCodes;
    setSearchParams(mutableSearchParams);
  }, [searchParams, customPackageSearchStore.searchParams]);

  const customIndexedDestinationSelect = useCallback(
    (store: PackageSearchStore) => (
      <CustomIndexedDestinationSelect
        store={store}
        urlEnrichmentData={urlEnrichmentData}
        onDestinationChange={onDestinationChange}
      />
    ),
    [urlEnrichmentData, onDestinationChange]
  );

  const onPackageSearchChange = ({
    changeType
  }: {
    store?: PackageSearchStore;
    changeType: 'duration' | 'Date' | 'participants' | 'origins' | 'destination';
  }) => {
    let mutableSearchParams = searchParams || {}; // There is a specific reason for this: to avoid unnecessary re-rendering.
    if (changeType === 'Date') {
      mutableSearchParams.startDate = customPackageSearchStore.searchParams.startDate;
      mutableSearchParams.endDate = customPackageSearchStore.searchParams.endDate;
    }

    if (changeType === 'duration') {
      const { startDate, duration } = customPackageSearchStore.searchParams;
      const numericDuration = Number(duration);

      if (
        startDate &&
        dayjs(startDate).isValid() &&
        Number.isFinite(numericDuration) &&
        numericDuration > 0
      ) {
        const endDate = dayjs(startDate).add(numericDuration, 'day').format(config.formatDate);

        customPackageSearchStore.setParams({
          endDate
        });

        setSearchParams(prev => ({
          ...prev,
          duration,
          endDate
        }));
      } else {
        setSearchParams(prev => ({
          ...prev,
          duration
        }));
      }

      return;
    }

    if (changeType === 'origins') {
      mutableSearchParams.origins = customPackageSearchStore.searchParams.origins;
    }
    if (changeType === 'participants') {
      mutableSearchParams.occupancy = customPackageSearchStore.searchParams.occupancy;
      setIsOpenMaxOccupancyModal(showModalOrNot(customPackageSearchStore, store, ysnFlightOnly));
      if (!areNumberOfTravelersBiggerThanMax(customPackageSearchStore, store)) {
        setYsnFlightOnly(false);
      }
    }
    setSearchParams(mutableSearchParams);
  };

  const getDisabledSortOptions = useMemo(() => {
    const hasPromotion = store.list.some(packageItem =>
      packageItem.price.modifiers.some(
        priceModifier => priceModifier.type === ApiPriceModifierType.DISCOUNT
      )
    );
    return hasPromotion ? undefined : [ApiSortOption.PROMOTIONS];
  }, [store.list]);
  type ApiFilterState = Partial<Record<ApiFilterCategory, boolean>>;
  const showResultsCount: ApiFilterState = {
    SERVICE_OPTIONS: false,
    DEPARTURE_AIRPORT: false,
    DESTINATION: false
  };

  return (
    <PrintTableWrapper>
      <div className={visibilityClassName}>
        {searchParams?.itemId && (
          <Button className="hide-in-print mt-3 back-button" onClick={handleBackToListClick}>
            {t(Keys.backToList)}
          </Button>
        )}
        {!!searchParams && (
          <PackageModule
            recoverMissingPackage
            paxRoomSelectionImperativeHandleRef={paxRoomSelectionImperativeHandleRef}
            listOptions={searchParams.listOptions}
            onBestPriceAvailabilitySelect={onBestPriceAvailabilitySelect}
            onSearch={updateUrlParamsOrShowModal}
            onDetail={updateUrlParams}
            searchParams={searchParams}
            customPackageSearchStore={customPackageSearchStore}
            showLabels
            subType={ApiItemType.PREDEFINEDPACKAGE}
            showMultipleDurations={false}
            showTotalPriceOnly
            destinationMandatory
            endDateMandatory
            withDateRangePicker
            withAdvancedDateRangePicker
            withGroupRequestForm={false}
            withRoomCountSelector={configService.get().enableMultipleRooms}
            renderSearch={search}
            renderSearchContent={RenderSearchContent}
            sortingType={{
              GROUP: [],
              SINGLE: [ApiSortOption.NAME, ApiSortOption.PRICE, ApiSortOption.PROMOTIONS]
            }}
            sorting={[ApiSortOption.NAME, ApiSortOption.PRICE, ApiSortOption.PROMOTIONS]}
            minDateRangeSpan={2}
            maxDateRangeSpan={200}
            renderListItem={RenderListItem}
            minSelectableChildAge={0}
            noDatePickerOverflow
            selectLabelSort
            withChildrenSelection
            bestPriceSubmitOnChange
            serviceSearchWithChildAgePicker
            changeOnBlur
            widgetClickable={true}
            packageOriginMustBeSet={false}
            packageOriginWithRemoveAll
            packageOriginWithSelectAll
            internalFlightLogo={internalFlightLogo?.['@link']}
            showInternalFlightLogo={true}
            customIndexedDestinationSelect={customIndexedDestinationSelect}
            dateRangeDurationProps={durationProps}
            dateRangeUpdateStateFromProps
            dateRangeFillWithLastDateRangeWhenNoSelection
            doBestPriceSearch
            bestPriceAfterSearch={shallowUrlUpdate}
            bestPriceMaxDateRangeSpan={42}
            bestPriceRenderSearchContent={BestPriceSearchContent}
            bestPriceDesktopSearchCollapse={false}
            bestPriceStickySearch
            ExternalBestPriceDetailsContent={DetailContent}
            hideSearchInDetails
            bestPriceListSelectionsMode
            key={keys[0]}
            includeCheapestFlightInRoomPrice
            splitRequiredComponentPricePerPerson
            customSetIsLoading={setIsLoading}
            bestPriceShowDirectFlightsOnlyFilter={configService.get().showDirectFlightsFilter}
            customListOptions={{
              showFilterInModal: true,
              showApplyButtonInModal: true,
              showResultsCountInParenthesis: true,
              showResultsCount: showResultsCount,
              showEmptyOptionsWhenFiltering: true,
              disabledSortOptions: getDisabledSortOptions,
              showResetButtonForFilter: true
            }}
            paginationProps={{
              onPageChangeListener
            }}
            showSearchParamsPerItem
            showDateSelectionInModal={isMobile}
            showPaxSelectionInModal={isMobile}
            paxSelectionRevalidateChildrenOnChangeAfterError
            paxSelectionValidateChildrenPaxSelectionOnChange
            paxSelectionUpdatePaxOnChangeWhenValid
            store={store}
            onPackageSearchChange={onPackageSearchChange}
            singleSelectCategories
            childAgePickerHeader={t(Keys.childAgePickerHeader)}
            scrollOnInitialSearch={false}
            useGeoUnitTwoColumnStyling
          />
        )}
        {searchParams?.itemId && (
          <Button className="hide-in-print back-button" onClick={handleBackToListClick}>
            {t(Keys.backToList)}
          </Button>
        )}
        <Modal
          isOpen={hasBookingError}
          toggle={() => setHasBookingError(!hasBookingError)}
          container={document.getElementById('iso') || undefined}
        >
          <ModalBody>
            <div className="text-center">{t(Keys.bookingErrorModalBody)}</div>
          </ModalBody>
          <ModalFooter className="justify-content-center">
            <Button color="primary" onClick={(): void => setHasBookingError(false)}>
              {t(Keys.bookingErrorModalButton)}
            </Button>
          </ModalFooter>
        </Modal>
        <InternalFlightModalWarning
          isOpen={isOpenMaxOccupancyModal}
          setIsOpenMaxOccupancyModal={setIsOpenMaxOccupancyModal}
          isSearchButtonClicked={isSearchButtonClicked}
          onNext={() => {
            setYsnFlightOnly(true);
            setIsOpenMaxOccupancyModal(false);
          }}
          onNextSearch={() => {
            setIsSearchButtonClicked(false);
            setIsOpenMaxOccupancyModal(false);
            updateUrlParams(customPackageSearchStore.searchParams, store.listOptions);
          }}
          onClose={() => {
            if (paxRoomSelectionImperativeHandleRef.current?.toggleDropdownOnly) {
              paxRoomSelectionImperativeHandleRef.current.toggleDropdownOnly(true);
            }
          }}
        />
        {isLoading && (
          <Overlay
            className="best-price__list-selections__spinner"
            positionFixed={true}
            text={t(Keys.onContinueLoadingText)}
            zIndex={1020}
            customSpinner={
              <div className="best-price-custom-spinner">
                <div />
                <div />
                <div />
                <div />
              </div>
            }
          />
        )}
      </div>
    </PrintTableWrapper>
  );
});

export default Ibe;
